import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Globe, Check, Quote, BookOpen, Brain, MessageSquare, TrendingUp, AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { logSecurityEvent } from '../../services/securityService';
import { trackLogin } from '../../utils/ga';

const loginAnimStyles = `
  @keyframes fadeSlideDown {
    from { opacity: 0; transform: translateY(-32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.7); }
    to   { opacity: 1; transform: scale(1); }
  }
  .login-anim-logo      { animation: scaleIn     0.55s cubic-bezier(0.34,1.56,0.64,1) both; }
  .login-anim-header    { animation: fadeSlideDown 0.6s ease both 0.15s; }
  .login-anim-quote     { animation: fadeSlideUp  0.65s ease both 0.3s; }
  .login-anim-card      { animation: fadeSlideUp  0.7s ease both 0.5s; }
  .login-anim-features  { animation: fadeIn       0.8s ease both 0.75s; }
`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useAuth();

  const {
    signIn = null,
    signInWithGoogle = null,
    loading = true,
    authReady = false,
    user = null
  } = authContext || {};

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese'];

  useEffect(() => {
    if (user && !loading) {
      const from = location?.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const isAuthServiceAvailable = () => {
    return authContext &&
    signIn &&
    typeof signIn === 'function' &&
    authReady;
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading || !email?.trim()) return;
    setResendLoading(true);
    setResendSuccess(false);
    try {
      // Use only the edge function for resend — it generates a fresh link and sends branded email.
      // Do NOT also call supabase.auth.resend here, as that would create two competing tokens.
      const { error: resendError } = await supabase?.functions?.invoke('send-verification-email', {
        body: { email: email?.trim() },
      });
      if (!resendError) {
        setResendSuccess(true);
        setResendCooldown(60);
      } else {
        // Fallback to Supabase built-in resend if edge function fails
        const { error: fallbackError } = await supabase?.auth?.resend({
          type: 'signup',
          email: email?.trim(),
        });
        if (!fallbackError) {
          setResendSuccess(true);
          setResendCooldown(60);
        }
      }
    } catch (err) {
      console.warn('Resend failed:', err?.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    setShowResendVerification(false);
    setResendSuccess(false);

    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!password?.trim()) {
      setError('Please enter your password.');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await signIn(email, password);
      if (error) {
        const errMsg = error?.message || '';
        // Log failed login attempt
        // We don't have user_id here for failed attempts, so we skip logging
        if (errMsg?.includes('Network') || errMsg?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else if (errMsg?.includes('timeout')) {
          setError('Connection timeout. Please try again.');
        } else if (errMsg?.includes('Email not confirmed') || errMsg?.includes('email_not_confirmed')) {
          setError('Your email address has not been verified yet. Please check your inbox or resend the verification email.');
          setShowResendVerification(true);
        } else {
          setError(errMsg || 'Failed to sign in');
        }
        return;
      }

      // Log successful login
      if (data?.user?.id) {
        logSecurityEvent(data?.user?.id, 'login', 'success', { method: 'email' });
        trackLogin('email');
      }

      const from = location?.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
      return;
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle?.();
      if (error) {
        setError(error?.message || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred with Google sign-in.');
    } finally {
      setGoogleLoading(false);
    }
  };

  if (!authContext || !authReady && loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication service...</p>
          <p className="text-muted-foreground text-sm mt-2">Please wait while we prepare your login experience</p>
          <div className="mt-4">
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 max-w-md mx-auto">
              <p className="text-primary text-xs">🔧 First time setup may take a few extra seconds</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authReady && !signIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center bg-card rounded-lg shadow-lg p-8 max-w-md mx-4 border border-border">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Authentication Service Error</h1>
          <p className="text-muted-foreground mb-4">Unable to initialize the authentication service.</p>
          <button onClick={() => window.location?.reload()} className="bg-destructive text-destructive-foreground px-6 py-2 rounded-lg hover:opacity-90 transition-opacity">
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{loginAnimStyles}</style>
      <div className="min-h-screen flex items-center justify-center relative py-8 px-4 sm:px-6 lg:px-8">
        {/* Background Image */}
        <div
          className="fixed inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/images/FortifiedMindSplash1-1769379659227.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'brightness(0.7)'
          }} />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex justify-center">
          <div className="w-full max-w-2xl space-y-6">
            {/* App Header */}
            <div className="text-center login-anim-header">
              <div className="mx-auto h-80 w-80 rounded-full flex items-center justify-center mb-6 shadow-2xl login-anim-logo overflow-hidden">
                <img
                  src="/assets/images/fm-color-full-1779117773822.png"
                  alt="Fortified Mind logo - castle and open Bible emblem"
                  style={{
                    width: '130%',
                    height: '122%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    marginLeft: '-15%',
                    marginTop: '-11%'
                  }}
                />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-2xl">FORTIFIED MIND</h1>
              <p className="text-white text-lg sm:text-xl mb-2 drop-shadow-lg font-semibold">Scripture Memorization & Reflection</p>
              <p className="text-gray-100 text-base sm:text-lg drop-shadow-lg">Strengthen your faith through God's Word</p>
            </div>

            {/* Ellen White Quote Section */}
            <div className="bg-primary/20 backdrop-blur-sm rounded-2xl shadow-2xl p-5 sm:p-6 text-white border border-white/20 login-anim-quote">
              <div className="flex items-start space-x-4">
                <Quote className="w-7 h-7 sm:w-8 sm:h-8 text-primary/80 flex-shrink-0 mt-1" />
                <div>
                  <blockquote className="text-base sm:text-lg italic leading-relaxed mb-3">
                    "None but those who have <span className="font-bold text-warning bg-warning/20 px-1 rounded">fortified</span> the <span className="font-bold text-warning bg-warning/20 px-1 rounded">mind</span> with the truths of the Bible will stand through the last great conflict."
                  </blockquote>
                  <cite className="text-white/70 text-sm font-medium">— Great Controversy pg. 593</cite>
                </div>
              </div>
            </div>

            {/* Login Card */}
            <div className="backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5 border border-white/30 bg-[rgba(255,255,255,0.6)] login-anim-card">

              {/* Error Display */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-destructive font-medium">Authentication Error</p>
                    <p className="text-destructive/80 text-sm">{error}</p>
                    {/* Resend verification link */}
                    {showResendVerification && (
                      <div className="mt-3 pt-3 border-t border-destructive/20">
                        {resendSuccess ? (
                          <div className="flex items-center space-x-2 text-success text-sm">
                            <Check className="w-4 h-4" />
                            <span>Verification email sent! Check your inbox.</span>
                          </div>
                        ) : (
                          <button
                            onClick={handleResendVerification}
                            disabled={resendCooldown > 0 || resendLoading || !email?.trim()}
                            className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {resendLoading ? (
                              <>
                                <div className="animate-spin h-3.5 w-3.5 border-2 border-primary border-t-transparent rounded-full" />
                                <span>Sending...</span>
                              </>
                            ) : resendCooldown > 0 ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5" />
                                <span>Resend in {resendCooldown}s</span>
                              </>
                            ) : (
                              <>
                                <Mail className="w-3.5 h-3.5" />
                                <span>Resend verification email</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex items-center space-x-3">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-success font-medium">Success!</p>
                    <p className="text-success/80 text-sm">{success}</p>
                  </div>
                </div>
              )}

              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="w-full flex items-center justify-center space-x-3 py-3 px-4 border-2 border-gray-300 rounded-xl bg-white hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-gray-700 shadow-sm min-h-[52px] touch-manipulation"
              >
                {googleLoading ? (
                  <span className="flex items-center space-x-2">
                    <span className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                    <span>Signing in with Google...</span>
                  </span>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300/60" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white/60 text-gray-500 rounded">or sign in with email</span>
                </div>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  floating
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                  className="w-full text-base touch-manipulation" />

                <Input
                  floating
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  required
                  className="w-full text-base touch-manipulation" />

                {/* Forgot Password Link */}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80 hover:underline transition-colors font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || loading}
                  className="w-full bg-primary text-primary-foreground py-4 sm:py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 text-base font-semibold min-h-[52px] touch-manipulation">
                  {isLoading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                      <span>Signing in...</span>
                    </span>
                  ) : 'Sign In'}
                </Button>
              </form>

              {/* Create Account Link */}
              <div className="text-center mt-4">
                <p className="text-sm text-foreground/70 mb-3">Don't have an account?</p>
                <Link
                  to="/signup"
                  className="inline-block w-full bg-success text-success-foreground font-bold text-base py-4 px-6 rounded-xl hover:opacity-90 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation min-h-[52px] flex items-center justify-center">
                  Create Free Account
                </Link>
                <p className="text-xs text-success font-semibold mt-2">✓ No credit card required • ✓ Always free</p>
              </div>

              {/* Language Selection */}
              <div className="border-t border-border/40 pt-4">
                <div className="flex items-center justify-center">
                  <Globe className="h-4 w-4 text-foreground/50 mr-2" />
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e?.target?.value)}
                    className="text-sm text-foreground/70 bg-transparent border-none focus:outline-none cursor-pointer disabled:opacity-50 py-2 touch-manipulation"
                    disabled={isLoading || !isAuthServiceAvailable()}>
                    {languages?.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Security & Privacy Notice */}
              <div className="text-center text-xs text-foreground/50 space-y-1">
                <p>Protected by industry-standard security measures</p>
                <div className="flex justify-center space-x-4">
                  <Link to="/privacy-policy" className="hover:text-foreground/80 underline py-1 touch-manipulation">Privacy Policy</Link>
                  <Link to="/terms-of-service" className="hover:text-foreground/80 underline py-1 touch-manipulation">Terms of Service</Link>
                </div>
              </div>
            </div>

            {/* App Features Section */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl shadow-2xl p-6 border-l-4 border-white border border-white/30 login-anim-features">
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Master Scripture Through Memorization & Reflection</h2>
                <p className="text-gray-700 leading-relaxed">
                  Fortified Mind is designed to help you internalize God's Word through systematic memorization and deep reflection.
                </p>
                <div className="flex items-center space-x-2 text-success">
                  <Check className="w-5 h-5" />
                  <span className="font-semibold text-lg">Complete Bible Study Suite - Always FREE!</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-3 p-3 bg-primary/10 rounded-lg">
                    <Brain className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Scripture Memorization System</span>
                      <p className="text-xs text-gray-600">Advanced spaced repetition techniques</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-accent/10 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-accent flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Reflection & Journaling</span>
                      <p className="text-xs text-gray-600">Personal insights and spiritual growth</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-success/10 rounded-lg">
                    <BookOpen className="w-5 h-5 text-success flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">SDA Bible Commentary</span>
                      <p className="text-xs text-gray-600">Complete commentary integration</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-warning/10 rounded-lg">
                    <Quote className="w-5 h-5 text-warning flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Ellen G. White Comments</span>
                      <p className="text-xs text-gray-600">Inspired insights on every passage</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-primary/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Progress Tracking</span>
                      <p className="text-xs text-gray-600">Monitor memorization milestones</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-destructive/10 rounded-lg">
                    <Check className="w-5 h-5 text-destructive flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-900">Interactive Reading Plans</span>
                      <p className="text-xs text-gray-600">Structured Bible study programs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}